import { Box } from '@chakra-ui/react';
import { Outlet } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { MobileDrawer } from './MobileDrawer';
import ForgeViewerMobile from './ForgeViewerMobile';
import { SelectedEntryContext } from './SelectedEntryContext';
import { CachedEntry } from './MobileOrderEntries';

export default function MobileProjectPage() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Helper function to get all descendant IDs recursively
  const getAllDescendantIds = useCallback((entry: CachedEntry): string[] => {
    const ids: string[] = [entry.id];
    if (entry.subRows) {
      entry.subRows.forEach((subRow) => {
        ids.push(...getAllDescendantIds(subRow));
      });
    }
    return ids;
  }, []);

  // Helper function to find parent of an entry
  const findParent = useCallback(
    (entryId: string, rootEntries: CachedEntry[]): CachedEntry | null => {
      for (const rootEntry of rootEntries) {
        const checkEntry = (entry: CachedEntry): CachedEntry | null => {
          if (entry.subRows) {
            for (const subRow of entry.subRows) {
              if (subRow.id === entryId) {
                return entry;
              }
              const found = checkEntry(subRow);
              if (found) return found;
            }
          }
          return null;
        };
        const found = checkEntry(rootEntry);
        if (found) return found;
      }
      return null;
    },
    []
  );

  // Check if an entry is selected
  const isSelected = useCallback(
    (entryId: string): boolean => {
      return selectedIds.has(entryId);
    },
    [selectedIds]
  );

  // Check if an entry is indeterminate (some but not all children selected)
  const isIndeterminate = useCallback(
    (entry: CachedEntry): boolean => {
      if (!entry.subRows || entry.subRows.length === 0) {
        return false;
      }

      const childIds = entry.subRows.map((subRow) => subRow.id);
      const selectedChildren = childIds.filter((id) => selectedIds.has(id));

      return (
        selectedChildren.length > 0 && selectedChildren.length < childIds.length
      );
    },
    [selectedIds]
  );

  // Select/deselect an entry with hierarchical logic
  const selectEntry = useCallback(
    (entry: CachedEntry, allRootEntries: CachedEntry[]) => {
      const newSelectedIds = new Set(selectedIds);
      const isCurrentlySelected = selectedIds.has(entry.id);

      if (isCurrentlySelected) {
        // Deselect this entry and all its descendants
        const allDescendantIds = getAllDescendantIds(entry);
        allDescendantIds.forEach((id) => newSelectedIds.delete(id));
      } else {
        // Select this entry and all its descendants
        const allDescendantIds = getAllDescendantIds(entry);
        allDescendantIds.forEach((id) => newSelectedIds.add(id));
      }

      // Update parent states based on their children
      const updateParentStates = (rootEntries: CachedEntry[]) => {
        rootEntries.forEach((rootEntry) => {
          const updateEntry = (currentEntry: CachedEntry) => {
            if (currentEntry.subRows && currentEntry.subRows.length > 0) {
              // First, recursively update children
              currentEntry.subRows.forEach(updateEntry);

              // Then update this entry based on its children
              const childIds = currentEntry.subRows.map((child) => child.id);
              const selectedChildCount = childIds.filter((id) =>
                newSelectedIds.has(id)
              ).length;

              if (selectedChildCount === childIds.length) {
                // All children selected -> select parent
                newSelectedIds.add(currentEntry.id);
              } else if (selectedChildCount === 0) {
                // No children selected -> deselect parent
                newSelectedIds.delete(currentEntry.id);
              } else {
                // Some children selected -> deselect parent (indeterminate state)
                newSelectedIds.delete(currentEntry.id);
              }
            }
          };
          updateEntry(rootEntry);
        });
      };

      updateParentStates(allRootEntries);
      setSelectedIds(newSelectedIds);
    },
    [selectedIds, getAllDescendantIds]
  );

  // Select only this entry (replace selection behavior)
  const selectOnlyEntry = useCallback(
    (entry: CachedEntry) => {
      // Clear all selections and select only this entry and its descendants
      const allDescendantIds = getAllDescendantIds(entry);
      setSelectedIds(new Set(allDescendantIds));
    },
    [getAllDescendantIds]
  );

  return (
    <SelectedEntryContext.Provider
      value={{
        selectedIds,
        selectEntry,
        selectOnlyEntry,
        isSelected,
        isIndeterminate,
      }}
    >
      <Box height="100vh" width="100vw" overflow="hidden" position="relative">
        <ForgeViewerMobile />
        <MobileDrawer open={true} initialPosition="bottom">
          <Outlet /> {/* Child routes will render here */}
        </MobileDrawer>
      </Box>
    </SelectedEntryContext.Provider>
  );
}
